import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Admin, AdminRole } from '@/types/admin';
import { Json } from '@/integrations/supabase/types';

const ADMIN_CACHE_KEY = 'vesper_admin_cache';

interface CachedAdmin {
  admin: Admin | null;
  userId: string;
  timestamp: number;
}

// Helper to convert Json to string[]
function parsePermissions(permissions: Json): string[] {
  if (Array.isArray(permissions)) {
    return permissions.filter((p): p is string => typeof p === 'string');
  }
  return [];
}

function getCachedAdmin(userId: string): Admin | null {
  try {
    const cached = localStorage.getItem(ADMIN_CACHE_KEY);
    if (!cached) return null;
    
    const data: CachedAdmin = JSON.parse(cached);
    // Only use cache if it's for the same user and less than 1 hour old
    if (data.userId === userId && Date.now() - data.timestamp < 3600000) {
      return data.admin;
    }
  } catch {
    // Ignore cache errors
  }
  return null;
}

function setCachedAdmin(userId: string, admin: Admin | null) {
  try {
    const data: CachedAdmin = {
      admin,
      userId,
      timestamp: Date.now(),
    };
    localStorage.setItem(ADMIN_CACHE_KEY, JSON.stringify(data));
  } catch {
    // Ignore cache errors
  }
}

// Database row type for admins with role join
interface AdminDbRow {
  id: string;
  user_id: string | null;
  role_id: string | null;
  is_active: boolean | null;
  created_at: string | null;
  updated_at: string | null;
  role: {
    id: string;
    name: string;
    display_name: string;
    permissions: Json;
    created_at: string | null;
  } | null;
}

function mapDbRowToAdmin(row: AdminDbRow): Admin {
  return {
    id: row.id,
    user_id: row.user_id ?? '',
    role_id: row.role_id ?? '',
    is_active: row.is_active ?? false,
    created_at: row.created_at ?? '',
    updated_at: row.updated_at ?? '',
    role: row.role ? {
      id: row.role.id,
      name: row.role.name,
      display_name: row.role.display_name,
      permissions: parsePermissions(row.role.permissions),
      created_at: row.role.created_at ?? '',
    } : undefined,
  };
}

export function useAdmin() {
  const { user, loading: authLoading } = useAuth();
  
  // Initialize with cached data for optimistic loading
  const [admin, setAdmin] = useState<Admin | null>(() => {
    if (user?.id) {
      return getCachedAdmin(user.id);
    }
    return null;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Wait for auth to be ready
    if (authLoading) return;

    // If no user, definitely not an admin
    if (!user) {
      setAdmin(null);
      setLoading(false);
      return;
    }

    // Check cache first for optimistic loading
    const cached = getCachedAdmin(user.id);
    if (cached) {
      setAdmin(cached);
      // Don't set loading to false yet - still need to verify
    }

    async function fetchAdmin() {
      try {
        const { data, error } = await supabase
          .from('admins')
          .select(`
            *,
            role:admin_roles(
              id,
              name,
              display_name,
              permissions,
              created_at
            )
          `)
          .eq('user_id', user!.id)
          .eq('is_active', true)
          .maybeSingle();

        if (error) {
          throw error;
        }

        const adminData = data ? mapDbRowToAdmin(data as unknown as AdminDbRow) : null;
        setAdmin(adminData);
        setCachedAdmin(user!.id, adminData);
      } catch (err) {
        console.error('Error fetching admin:', err);
        setError(err instanceof Error ? err : new Error('Unknown error'));
        // Keep cached data on error
      } finally {
        setLoading(false);
      }
    }

    fetchAdmin();
  }, [user, authLoading]);

  return { admin, loading: loading || authLoading, error };
}
